import { useCallback, useEffect, useState } from 'react';
import { TENANT_VIEWS } from '../../constants/tenant';
import { useIsSingleTenant } from '../../hooks/organization';
import { getCurrentTenant, getSubtenants } from '../../sources/tenant';
import translations from '../../translations';
import type { TenantResponseAPI } from '../../types/Tenant';
import type { UserTenantResponseAPI } from '../../types/user';
import { FeatureFlag } from '../../utils/featureFlags';
import { isSubtenant } from '../../utils/tenant';
import { useFeatureFlag } from '../featureFlags/FeatureFlagProvider';
import PageSwitcher from '../generic/PageSwitcher';
import { useNotificationDispatch } from '../notifications/NotificationProvider';
import CreateSubtenant from './CreateSubtenant';
import TenantPage from './TenantPage';

type TenantPageSwitcherProps = {
    tenant: UserTenantResponseAPI;
};

const TenantPageSwitcher = ({ tenant }: TenantPageSwitcherProps) => {
    const { getFlag } = useFeatureFlag();
    const [currentTenant, setCurrentTenant] = useState<TenantResponseAPI>();

    // In "Account mode" (single-tenant organizations with the ORGACC flag on) the screen is
    // relabelled to hide the tenant concept and the Local Runtimes section is surfaced here.
    const accountMode = useIsSingleTenant(tenant);

    const [subtenants, setSubtenants] = useState<TenantResponseAPI[]>([]);

    const dispatchNotification = useNotificationDispatch();

    useEffect(() => {
        const fetchCurrentTenant = async () => {
            try {
                // Only users with the administrator role can access the tenant API endpoint
                const fetchedTenant = await getCurrentTenant();

                setCurrentTenant(fetchedTenant);
            } catch (error) {
                dispatchNotification({
                    type: 'notifyError',
                    payload: { error },
                });
            }
        };

        fetchCurrentTenant();
    }, [dispatchNotification]);

    const fetchSubtenants = useCallback(async () => {
        try {
            const fetchedSubtenants = await getSubtenants();

            setSubtenants(fetchedSubtenants);
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: { error },
            });
        }
    }, [dispatchNotification]);

    useEffect(() => {
        fetchSubtenants();
    }, [fetchSubtenants]);

    const shouldShowSubtenantsList = () => {
        if (isSubtenant(tenant.developerName)) return false;

        if (getFlag(FeatureFlag.SUBTNT) && subtenants.length === 0) return false;

        return true;
    };

    // Single-tenant accounts (ORGACC) inherit single sign-on users from the platform, so the
    // Flow-specific SSO configuration is hidden — unless SSO has already been configured for this
    // tenant, in which case it stays visible so the existing setup remains manageable.
    const shouldShowSingleSignOn = !accountMode || !!currentTenant?.securitySettings?.isSamlEnabled;

    // Hide the environment section for single-tenant organizations (when the
    // ORGACC feature flag is enabled) that already have environments turned on.
    const shouldShowEnvironmentOption = !accountMode || !tenant?.tenantSettings?.environments;

    const shouldShowTenantImport = getFlag(FeatureFlag.TNTIMP);

    return (
        <PageSwitcher
            pages={[
                {
                    id: TENANT_VIEWS.tenant,
                    label: accountMode
                        ? translations.TENANT_page_title_account
                        : translations.TENANT_page_title_tenant,
                    parent: null,
                    menu: [
                        {
                            label: accountMode
                                ? translations.TENANT_account_name_menu_label
                                : 'Tenant Name',
                            link: '#tenant-name',
                            visible: true,
                        },
                        {
                            label: 'Users',
                            link: '#tenant-users',
                            visible: true,
                        },
                        {
                            label: 'Environments',
                            link: '#tenant-environment',
                            visible: shouldShowEnvironmentOption,
                        },
                        {
                            label: 'Themes',
                            link: '#tenant-themes',
                            visible: true,
                        },
                        {
                            label: 'Integration Accounts',
                            link: '#tenant-integration-accounts',
                            visible: true,
                        },
                        {
                            label: 'Subtenants',
                            link: '#tenant-subtenants',
                            visible: shouldShowSubtenantsList(),
                        },
                        {
                            label: 'Single Sign-on',
                            link: '#tenant-single-sign-on',
                            visible: shouldShowSingleSignOn,
                        },
                        {
                            label: 'Security',
                            link: '#tenant-security',
                            visible: true,
                        },
                        {
                            label: 'Observability',
                            link: '#tenant-observability',
                            visible: true,
                        },
                        {
                            label: 'Export',
                            link: '#tenant-export',
                            visible: true,
                        },
                        {
                            label: 'Import',
                            link: '#tenant-import',
                            visible: shouldShowTenantImport,
                        },
                        {
                            label: 'Comments',
                            link: '#tenant-comments',
                            visible: true,
                        },
                        {
                            label: translations.LOCAL_RUNTIME_title,
                            link: '#localRuntimes',
                            visible: accountMode,
                        },
                    ],
                    component: TenantPage,
                    subtenants,
                    shouldShowSubtenantsList: shouldShowSubtenantsList(),
                    shouldShowSingleSignOn,
                },
                {
                    id: TENANT_VIEWS.newSubtenant,
                    label: translations.TENANT_page_title_create_subtenant,
                    parent: 'tenant',
                    menu: null,
                    component: CreateSubtenant,
                    onSubtenantCreated: fetchSubtenants,
                },
            ]}
        />
    );
};

export default TenantPageSwitcher;
